import { useEffect, useState } from "react";
import { useAssessmentSession } from "../contexts/assessmentUserContext";
import {
  type SchoolOption,
  fetchSchools,
} from "../services/appSchoolService";

type UseSchoolsFilterParams = {
  districtIds: string[];
  gradeIds?: string[];
};

const EMPTY_GRADE_IDS: string[] = [];

export function useSchoolsFilter({
  districtIds,
  gradeIds = EMPTY_GRADE_IDS,
}: UseSchoolsFilterParams) {
  const { token } = useAssessmentSession();
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const districtKey = districtIds.join(",");
  const gradeKey = gradeIds.join(",");

  useEffect(() => {
    if (!token || districtKey.length === 0) {
      setSchools([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const districtIdsForRequest = districtKey.split(",").filter(Boolean);
    const gradeIdsForRequest = gradeKey.length
      ? gradeKey.split(",").filter(Boolean)
      : EMPTY_GRADE_IDS;

    let cancelled = false;

    const loadSchools = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const nextSchools = await fetchSchools(token, {
          districtIds: districtIdsForRequest,
          gradeIds: gradeIdsForRequest,
        });
        if (!cancelled) {
          setSchools(nextSchools);
        }
      } catch (loadError: unknown) {
        if (!cancelled) {
          setSchools([]);
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Unable to load schools.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSchools().then();

    return () => {
      cancelled = true;
    };
  }, [token, districtKey, gradeKey]);

  return { schools, isLoading, error };
}
